import { createContext, useContext } from 'react';
import { useQuery } from '@apollo/client';

import {
  GetViewerProgramEnrollmentsWithCoverageDocument,
  ProgramEnrollmentStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import {
  FIT_HELP_CENTER_URL,
  FIT_SUPPORT_URL,
} from '@sandtech-hub-monorepo/ui-kit-site';

/**
 * Which half of the app a learner belongs to.
 *
 * `isUniversityProgram` on an enrollment is the only signal there is, and it
 * is enough: degree enrollments are never created here. Nothing in set-edu
 * calls `applyToProgram` for a university program — only the GSA detail page
 * calls it at all — so degree learners arrive already enrolled from
 * admissions, and there is no prospective-degree state with no enrollment for
 * this to strand.
 *
 * A learner with no enrollments is therefore GSA: it is the only path they can
 * start from inside this app.
 *
 * Reviewed and kept (PR #1120): this drops what master showed that learner —
 * `HomeNotEnrolled`, the degree institution page with `ProgramsPreview` — so
 * there is no route from signup to a degree programme page any more. That is
 * intended, not an oversight. Existing degree learners keep the platform they
 * already had, and new degree learners are enrolled by hand rather than
 * recruited through set-edu, so the funnel this removes had nobody to serve.
 * If self-serve degree signup is ever added here, this is the function that
 * has to learn a fourth state — "prospective" — because no enrollment can
 * then be read as a choice.
 */
export type LearnerPath = 'gsa' | 'degree' | 'both';

interface UseLearnerPathQueryResult {
  path: LearnerPath;
  isActiveUniversityStudent: boolean;
  loading: boolean;
}

/**
 * Statuses that mean a degree enrollment is still live — the student has not
 * left, whether or not they are currently taking classes. Narrower than
 * `hasDegree` above on purpose: this drives support/payments routing, not
 * page selection, and a GRADUATED alum has no business in either.
 */
const ACTIVE_ENROLLMENT_STATUSES: ProgramEnrollmentStatus[] = [
  ProgramEnrollmentStatus.Ongoing,
  ProgramEnrollmentStatus.Deferred,
  ProgramEnrollmentStatus.LeaveOfAbsence,
];

/**
 * Where a support link should send a learner. Degree students who are
 * actively enrolled get the university's own support desk; everyone else —
 * GSA learners, and degree learners without a live enrollment (e.g. an
 * alum) — gets the general FIT help center.
 */
export const supportUrlFor = (isActiveUniversityStudent: boolean): string =>
  isActiveUniversityStudent ? FIT_SUPPORT_URL : FIT_HELP_CENTER_URL;

/**
 * Resolves the path from the server. Call this once, in `GuardedRoutes`, and
 * read it everywhere else through `useLearnerPath`.
 *
 * Six components used to call it, which meant six `ObservableQuery` instances
 * on one document — and the guard, which has to resolve the path before it can
 * decide anything, had already done the work every one of them repeated.
 */
export const useLearnerPathQuery = ({
  skip = false,
} = {}): UseLearnerPathQueryResult => {
  const { data, loading } = useQuery(
    GetViewerProgramEnrollmentsWithCoverageDocument,
    { fetchPolicy: 'cache-first', skip }
  );

  // FAILED is the one status that does not count, matching
  // getDisplayableEnrollments on the degree side. Everything else does,
  // including GRADUATED: an alum keeps the surfaces their degree came with.
  const enrollments = (data?.viewer?.programEnrollments ?? []).filter(
    (row) => row.status !== ProgramEnrollmentStatus.Failed
  );

  const hasDegree = enrollments.some((row) => row.program.isUniversityProgram);
  const hasGsa = enrollments.some((row) => !row.program.isUniversityProgram);
  const isActiveUniversityStudent = enrollments.some(
    (row) =>
      row.program.isUniversityProgram &&
      ACTIVE_ENROLLMENT_STATUSES.includes(row.status)
  );

  return {
    path: hasDegree && hasGsa ? 'both' : hasDegree ? 'degree' : 'gsa',
    isActiveUniversityStudent,
    loading: !skip && loading && !data,
  };
};

interface LearnerPathContextValue {
  path: LearnerPath;
  isActiveUniversityStudent: boolean;
}

/**
 * Undefined outside the provider, so `useLearnerPath` can tell "no provider"
 * from a real value. Every consumer is a private route, and `GuardedRoutes`
 * provides it around the whole `Outlet`, so there is no legitimate way to read
 * this without one.
 */
const LearnerPathContext = createContext<LearnerPathContextValue | undefined>(
  undefined
);

export const LearnerPathProvider = LearnerPathContext.Provider;

const useLearnerPathContext = (): LearnerPathContextValue => {
  const value = useContext(LearnerPathContext);

  if (!value) {
    throw new Error(
      'useLearnerPath must be used inside GuardedRoutes, which resolves the path.'
    );
  }

  return value;
};

/**
 * The current learner's path. No query and no loading state: the guard has
 * already resolved it before this component could render, which is also why
 * the pages that call this no longer need a loading branch.
 */
export const useLearnerPath = (): LearnerPath => useLearnerPathContext().path;

/**
 * Whether the learner has a live (ONGOING/DEFERRED/LEAVE_OF_ABSENCE) degree
 * enrollment — see `supportUrlFor`'s doc comment for why this is narrower
 * than `useLearnerPath() === 'degree'`.
 */
export const useIsActiveUniversityStudent = (): boolean =>
  useLearnerPathContext().isActiveUniversityStudent;
